import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  DataGrid,
  DataGridHeader,
  DataGridRow,
  DataGridHeaderCell,
  DataGridBody,
  DataGridCell,
  TableColumnDefinition,
  TableCellLayout,
  createTableColumn,
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
  Badge,
  Text,
  Tooltip,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens
} from '@fluentui/react-components';
import {
  ArrowDownload24Regular,
  Attach24Regular,
  Delete24Regular,
  Edit24Regular,
  ServiceBellRegular
} from '@fluentui/react-icons';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Service } from '../../../models/Entities/Service';
import ConfirmationModalComponent from '../../../commons/component/ConfirmationModal/ConformationModal.Component';
import ServiceListMenu, { ServiceFilterCriteria } from './ServiceList.Menu';
import ServiceExportDrawer from '../ServiceExportDrawer';
import ServiceReceiptCountPill from '../ServiceReceipts/ServiceReceiptCountPill.Component';
import ServiceReceiptsDrawer from '../ServiceReceipts/ServiceReceiptsDrawer.Component';
import {
  SERVICE_ROW_ATTRIBUTE,
  getDropServiceId,
  getProtocolIdsToResolve,
  getServiceReceiptCount,
  getServiceReceiptTarget
} from '../ServiceReceipts/ServiceReceipts.Functions';
import { dataTransferHasFiles } from '../../../functions/AzureFileUpload.Functions';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';

// functions
import * as ComponentFunctions from './ServiceList.Functions';

//#region Status helpers
const getStatusColor = (statusId?: number): 'success' | 'warning' | 'informative' | 'danger' | 'subtle' => {
  switch (statusId) {
    case 1: return 'success';      // active
    case 2: return 'subtle';       // inactive
    case 3: return 'informative';  // completed
    case 4: return 'danger';       // cancelled
    default: return 'subtle';
  }
};

const getStatusLabel = (t: TFunction, statusId?: number): string => {
  switch (statusId) {
    case 1: return t('serviceForm.status.active');
    case 2: return t('serviceForm.status.inactive');
    case 3: return t('serviceForm.status.completed');
    case 4: return t('serviceForm.status.cancelled');
    default: return '—';
  }
};

const getFlagLabel = (t: TFunction, key: 'attachReceipt' | 'receivesPerDiem' | 'takesBreakfast'): string => {
  switch (key) {
    case 'attachReceipt': return t('serviceForm.formFields.attachReceipt');
    case 'receivesPerDiem': return t('serviceForm.formFields.receivesPerDiem');
    case 'takesBreakfast': return t('serviceForm.formFields.takesBreakfast');
    default: return key;
  }
};
//#endregion

export const ServiceListComponent: React.FC<ComponentFunctions.Props> = (props) => {
  const [t] = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filteredServices, setFilteredServices] = useState<Service[]>([]);
  const [isExportDrawerOpen, setIsExportDrawerOpen] = useState<boolean>(false);
  // Receipts drawer (contract §10.4): service whose receipts are open + files dropped on its row
  const [receiptsServiceId, setReceiptsServiceId] = useState<string | undefined>(undefined);
  const [receiptsDroppedFiles, setReceiptsDroppedFiles] = useState<File[] | undefined>(undefined);
  // File drag over the grid: whether files are being dragged and the row (service) that would receive them
  const [fileDrag, setFileDrag] = useState<{ active: boolean; serviceId?: string }>({ active: false });
  
  // Initialize filters from URL params
  const [filterCriteria, setFilterCriteria] = useState<ServiceFilterCriteria>(() => {
    const protocolId = searchParams.get('protocolId');
    const stageId = searchParams.get('stageId');
    const patientNumbers = searchParams.get('patientNumbers');
    const startDate = searchParams.get('startDate');
    const endDate = searchParams.get('endDate');
    
    return {
      protocolId: protocolId ? parseInt(protocolId) : undefined,
      stageId: stageId ? parseInt(stageId) : undefined,
      patientNumbers: patientNumbers ? patientNumbers.split(',') : [],
      startDate: startDate ? new Date(startDate) : undefined,
      endDate: endDate ? new Date(endDate) : undefined
    };
  });
  
  const styles = useStyles();

  // Sync filters with URL params when they change externally
  useEffect(() => {
    const protocolId = searchParams.get('protocolId');
    const stageId = searchParams.get('stageId');
    const patientNumbers = searchParams.get('patientNumbers');
    const startDate = searchParams.get('startDate');
    const endDate = searchParams.get('endDate');
    
    setFilterCriteria({
      protocolId: protocolId ? parseInt(protocolId) : undefined,
      stageId: stageId ? parseInt(stageId) : undefined,
      patientNumbers: patientNumbers ? patientNumbers.split(',') : [],
      startDate: startDate ? new Date(startDate) : undefined,
      endDate: endDate ? new Date(endDate) : undefined
    });
  }, [searchParams]);

  // Effects
  useEffect(() => {
    if (!props.successLoadingAll && !props.isLoadingAll && !props.failOnLoadingAll) {
      props.GetAllServices();
    }
  }, [props.successLoadingAll, props.isLoadingAll, props.failOnLoadingAll, props.GetAllServices]);

  useEffect(() => {
    if (props.services) {
      let filtered: Service[] = ComponentFunctions.filterServices(props.services, props.searchCriteria);
      
      // Apply additional filters
      if (filterCriteria.protocolId) {
        filtered = filtered.filter(s => s.protocolId === filterCriteria.protocolId);
      }
      if (filterCriteria.stageId) {
        filtered = filtered.filter(s => s.stageId === filterCriteria.stageId);
      }
      if (filterCriteria.patientNumbers && filterCriteria.patientNumbers.length > 0) {
        filtered = filtered.filter(s => 
          s.numberInProtocol && filterCriteria.patientNumbers!.includes(s.numberInProtocol)
        );
      }
      if (filterCriteria.startDate) {
        filtered = filtered.filter(s => {
          if (!s.startDate) return false;
          const serviceDate = new Date(s.startDate);
          return serviceDate >= filterCriteria.startDate!;
        });
      }
      if (filterCriteria.endDate) {
        filtered = filtered.filter(s => {
          if (!s.startDate) return false;
          const serviceDate = new Date(s.startDate);
          return serviceDate <= filterCriteria.endDate!;
        });
      }
      
      setFilteredServices(filtered);
    }
  }, [props.services, props.searchCriteria, filterCriteria]);

  // Sponsor of each visible protocol: owner of the receipts folder (resolved once per protocol, cached in the store)
  const { protocolSponsors, ResolveProtocolSponsor } = props;
  useEffect(() => {
    if (!ResolveProtocolSponsor) return;
    getProtocolIdsToResolve(filteredServices, protocolSponsors ?? {}).forEach((protocolId) =>
      ResolveProtocolSponsor(protocolId)
    );
  }, [filteredServices, protocolSponsors, ResolveProtocolSponsor]);

  const receiptsService = useMemo(
    () => (receiptsServiceId ? props.services.find((service) => service.id === receiptsServiceId) : undefined),
    [receiptsServiceId, props.services]
  );
  const receiptsTarget = getServiceReceiptTarget(receiptsService, protocolSponsors ?? {}).target;

  const openReceipts = useCallback((service: Service, files?: File[]) => {
    setReceiptsDroppedFiles(files);
    setReceiptsServiceId(service.id);
  }, []);

  const closeReceipts = () => {
    setReceiptsServiceId(undefined);
    setReceiptsDroppedFiles(undefined);
  };

  /** Row under the pointer that can receive the dropped files (its receipts target is available). */
  const getDroppableService = (target: EventTarget | null): Service | undefined => {
    const serviceId = getDropServiceId(target);
    const service = serviceId ? filteredServices.find((item) => item.id === serviceId) : undefined;
    return service && getServiceReceiptTarget(service, protocolSponsors ?? {}).target ? service : undefined;
  };

  // Files dropped on a ROW open the receipts of that service; anywhere else in the list nothing happens
  // (the browser must not open the file either) and a hint explains where to drop them.
  const onListDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (receiptsServiceId || !dataTransferHasFiles(event.dataTransfer)) return;
    event.preventDefault();
    const service = getDroppableService(event.target);
    if (event.dataTransfer) event.dataTransfer.dropEffect = service ? 'copy' : 'none';
    setFileDrag((current) =>
      current.active && current.serviceId === service?.id ? current : { active: true, serviceId: service?.id }
    );
  };

  const onListDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    const next = event.relatedTarget as Node | null;
    if (next && event.currentTarget.contains(next)) return;
    setFileDrag((current) => (current.active ? { active: false } : current));
  };

  const onListDrop = (event: React.DragEvent<HTMLDivElement>) => {
    if (receiptsServiceId || !dataTransferHasFiles(event.dataTransfer)) return;
    event.preventDefault();
    setFileDrag({ active: false });
    const service = getDroppableService(event.target);
    const files = Array.from(event.dataTransfer?.files ?? []);
    if (service && files.length > 0) openReceipts(service, files);
  };

  // Reload services after successful deletion
  useEffect(() => {
    if (props.isDeletingSuccess) {
      props.GetAllServices();
    }
  }, [props.isDeletingSuccess]);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (filterCriteria.protocolId) {
      params.set('protocolId', filterCriteria.protocolId.toString());
    }
    if (filterCriteria.stageId) {
      params.set('stageId', filterCriteria.stageId.toString());
    }
    if (filterCriteria.patientNumbers && filterCriteria.patientNumbers.length > 0) {
      params.set('patientNumbers', filterCriteria.patientNumbers.join(','));
    }
    if (filterCriteria.startDate) {
      params.set('startDate', filterCriteria.startDate.toISOString());
    }
    if (filterCriteria.endDate) {
      params.set('endDate', filterCriteria.endDate.toISOString());
    }
    
    setSearchParams(params, { replace: true });
  }, [filterCriteria, setSearchParams]);

  // Handler for confirming service deletion
  const handleConfirmDelete = (serviceId: string) => {
    props.DeleteService(serviceId);
  };

  const handleOpenExportDrawer = () => {
    setIsExportDrawerOpen(true);
  };

  const handleCloseExportDrawer = () => {
    setIsExportDrawerOpen(false);
  };

  // Define columns using Fluent UI v9 DataGrid
  const columns: TableColumnDefinition<Service>[] = useMemo(() => [
    createTableColumn<Service>({
      columnId: 'description',
      compare: (a, b) => (a.description || '').localeCompare(b.description || ''),
      renderHeaderCell: () => t('servicesListPage.columns.description'),
      renderCell: (item) => (
        <TableCellLayout>
          <div className={styles.cellPrimary}>{item.description || '—'}</div>
          {item.serviceCategoryDescription && (
            <div className={styles.cellSecondary}>{item.serviceCategoryDescription}</div>
          )}
          {item.consumerName && (
            <div className={styles.cellCaption}>{item.consumerName}</div>
          )}
        </TableCellLayout>
      ),
    }),
    createTableColumn<Service>({
      columnId: 'protocolId',
      compare: (a, b) => (a.protocolId || 0) - (b.protocolId || 0),
      renderHeaderCell: () => t('servicesListPage.columns.protocol'),
      renderCell: (item) => (
        <TableCellLayout>
          {item.protocolId ? (
            <div className={styles.cellPrimary}>#{item.protocolId}</div>
          ) : (
            <div className={styles.cellEmpty}>—</div>
          )}
          {(item.visitName || item.stageId) && (
            <div className={styles.cellSecondary}>
              {t('servicesListPage.columns.visit')}: {item.visitName || `#${item.stageId}`}
            </div>
          )}
          {item.numberInProtocol && (
            <div className={styles.cellCaption}>{t('servicesListPage.columns.numberInProtocol')}: {item.numberInProtocol}</div>
          )}
        </TableCellLayout>
      ),
    }),
    createTableColumn<Service>({
      columnId: 'startDate',
      compare: (a, b) => {
        const dateA = a.startDate ? new Date(a.startDate).getTime() : 0;
        const dateB = b.startDate ? new Date(b.startDate).getTime() : 0;
        return dateA - dateB;
      },
      renderHeaderCell: () => t('servicesListPage.columns.period'),
      renderCell: (item) => (
        <TableCellLayout>
          <div className={styles.cellPrimary}>
            {item.startDate ? new Date(item.startDate).toLocaleDateString() : '—'}
          </div>
          {item.endDate && (
            <div className={styles.cellSecondary}>
              {t('servicesListPage.columns.to')}: {new Date(item.endDate).toLocaleDateString()}
            </div>
          )}
        </TableCellLayout>
      ),
    }),
    createTableColumn<Service>({
      columnId: 'estimateCost',
      compare: (a, b) => (a.finalCost || 0) - (b.finalCost || 0),
      renderHeaderCell: () => t('servicesListPage.columns.cost'),
      renderCell: (item) => {
        const currency = item.currencyType || 'ARS';
        const final = item.finalCost ?? 0;
        const estimated = item.estimateCost ?? 0;
        const hasVariance = final !== estimated;
        return (
          <TableCellLayout>
            <div className={styles.cellCost}>{currency} {final.toFixed(2)}</div>
            {hasVariance && (
              <div className={styles.cellSecondary}>
                {t('servicesListPage.columns.estimated')}: {currency} {estimated.toFixed(2)}
              </div>
            )}
          </TableCellLayout>
        );
      },
    }),
    createTableColumn<Service>({
      columnId: 'serviceStatusId',
      compare: (a, b) => (a.serviceStatusId || 0) - (b.serviceStatusId || 0),
      renderHeaderCell: () => t('servicesListPage.columns.status'),
      renderCell: (item) => (
        <Badge appearance="tint" color={getStatusColor(item.serviceStatusId)} shape="rounded">
          {item.serviceStatusName || getStatusLabel(t, item.serviceStatusId)}
        </Badge>
      ),
    }),
    createTableColumn<Service>({
      columnId: 'serviceFlags',
      renderHeaderCell: () => t('servicesListPage.columns.flags', { defaultValue: 'Condiciones' }),
      renderCell: (item) => {
        const activeFlags: Array<'attachReceipt' | 'receivesPerDiem' | 'takesBreakfast'> = [];
        if (item.attachReceipt) activeFlags.push('attachReceipt');
        if (item.receivesPerDiem) activeFlags.push('receivesPerDiem');
        if (item.takesBreakfast) activeFlags.push('takesBreakfast');

        if (activeFlags.length === 0) {
          return <div className={styles.cellEmpty}>—</div>;
        }

        return (
          <div className={styles.flagsRow}>
            {activeFlags.map((flag) => (
              <Badge key={`${item.id}-${flag}`} appearance="outline" color="brand" shape="rounded">
                {getFlagLabel(t, flag)}
              </Badge>
            ))}
          </div>
        );
      },
    }),
    createTableColumn<Service>({
      columnId: 'receiptCount',
      compare: (a, b) => getServiceReceiptCount(a) - getServiceReceiptCount(b),
      renderHeaderCell: () => t('serviceReceipts.column'),
      renderCell: (item) => <ServiceReceiptCountPill count={getServiceReceiptCount(item)} />,
    }),
    createTableColumn<Service>({
      columnId: 'actions',
      renderHeaderCell: () => t('servicesListPage.columns.actions'),
      renderCell: (item) => (
        <div className={styles.actions}>
          {(() => {
            const { target, unavailableReasonKey } = getServiceReceiptTarget(item, protocolSponsors ?? {});
            const label = t('serviceReceipts.actionNamed', {
              description: item.description?.trim() || t('serviceReceipts.untitled')
            });
            return (
              <Tooltip
                content={unavailableReasonKey ? t(unavailableReasonKey) : t('serviceReceipts.actionTooltip')}
                relationship="description"
                withArrow
              >
                <Button
                  appearance="subtle"
                  icon={<Attach24Regular />}
                  aria-label={label}
                  onClick={() => openReceipts(item)}
                  disabledFocusable={!target}
                  data-testid="service-receipts-action"
                />
              </Tooltip>
            );
          })()}
          <Button
            appearance="subtle"
            icon={<Edit24Regular />}
            title={t('servicesListPage.actions.edit')}
            aria-label={t('servicesListPage.actions.edit')}
            onClick={() => props.onEdit && props.onEdit(item)}
          />
          <ConfirmationModalComponent
            onDismiss={() => {}}
            onConfirm={() => handleConfirmDelete(item.id)}
            title={t('servicesListPage.delete.title')}
            message={t('servicesListPage.delete.message', { description: item.description })}
            buttonApparence="subtle"
            buttonShape="circular"
            buttonTitle=""
            buttonIcon={<Delete24Regular />}
            isProcessing={props.isDeleting}
          />
        </div>
      ),
    }),
  ], [t, props.onEdit, props.isDeleting, protocolSponsors, openReceipts]);

  //#region Subcontent definition

  // Loading
  const Loading: JSX.Element | false = props.isLoadingAll && (
    <div className={styles.loading}>
      <Spinner size="large" label={t('servicesListPage.loading')} />
    </div>
  );

  // Error Message
  const ErrorMessage: JSX.Element | false = props.failOnLoadingAll && (
    <MessageBar intent="error">
      <MessageBarBody>
        {t('servicesListPage.error')}: {props.error?.ErrorMessage || t('servicesListPage.unknownError')}
      </MessageBarBody>
    </MessageBar>
  );

  // No Services Message
  const NoServicesMessage: JSX.Element | false = !props.isLoadingAll && filteredServices.length === 0 && (
    <div className={styles.emptyState}>
      <ServiceBellRegular className={styles.emptyStateIcon} />
      <Text size={500} weight="semibold" className={styles.emptyStateTitle}>
        {t('servicesListPage.noServices')}
      </Text>
    </div>
  );

  // Summary bar
  const SummaryBar: JSX.Element | false = !props.isLoadingAll && filteredServices.length > 0 && (
    <div className={styles.summaryBar}>
      <Text size={200} className={styles.summaryText}>
        {t('servicesListPage.summary', { count: filteredServices.length })}
      </Text>
    </div>
  );

  // Table of Content using DataGrid (Fluent UI v9)
  const TableOfContent: JSX.Element | false = !props.isLoadingAll && filteredServices.length > 0 && (
    <div
      className={styles.tableWrapper}
      onDragOver={onListDragOver}
      onDragLeave={onListDragLeave}
      onDrop={onListDrop}
      data-testid="service-list-drop-area"
    >
      {fileDrag.active && (
        <div className={styles.dropHint} aria-live="polite" data-testid="service-list-drop-hint">
          <GuideMessageBar intent="info">
            <MessageBarBody>
              {fileDrag.serviceId
                ? t('serviceReceipts.dropOnRow', {
                    description:
                      filteredServices.find((item) => item.id === fileDrag.serviceId)?.description?.trim() ||
                      t('serviceReceipts.untitled')
                  })
                : t('serviceReceipts.dropHint')}
            </MessageBarBody>
          </GuideMessageBar>
        </div>
      )}
      <DataGrid
        items={filteredServices}
        columns={columns}
        sortable
        getRowId={(item: Service) => item.id}
      >
        <DataGridHeader className={styles.tableHeader}>
          <DataGridRow>
            {({ renderHeaderCell }) => (
              <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
            )}
          </DataGridRow>
        </DataGridHeader>
        <DataGridBody<Service>>
          {({ item, rowId }) => (
            <DataGridRow<Service>
              key={rowId}
              className={mergeClasses(styles.tableRow, fileDrag.serviceId === item.id && styles.dropRow)}
              {...{ [SERVICE_ROW_ATTRIBUTE]: item.id }}
              data-drop-target={fileDrag.serviceId === item.id ? 'true' : undefined}
            >
              {({ renderCell }) => (
                <DataGridCell className={styles.tableCell}>{renderCell(item)}</DataGridCell>
              )}
            </DataGridRow>
          )}
        </DataGridBody>
      </DataGrid>
    </div>
  );

  //#endregion

  return (
    <div className={styles.container}>
      {props.isDeletingSuccess && (
        <MessageBar intent="success">
          <MessageBarBody>
            {t('servicesListPage.delete.success')}
          </MessageBarBody>
        </MessageBar>
      )}
      {ErrorMessage}
      <ServiceListMenu 
        onFilterChange={setFilterCriteria} 
        currentFilters={filterCriteria}
      />
      <div className={styles.exportActionContainer}>
        <Button
          appearance="secondary"
          icon={<ArrowDownload24Regular />}
          onClick={handleOpenExportDrawer}
        >
          {t('servicesListPage.export.button', { defaultValue: 'Exportar Excel' })}
        </Button>
      </div>
      {Loading}
      {SummaryBar}
      {NoServicesMessage}
      {TableOfContent}
      <ServiceReceiptsDrawer
        open={!!receiptsService && !!receiptsTarget}
        onDismiss={closeReceipts}
        service={receiptsService}
        target={receiptsTarget}
        initialFiles={receiptsDroppedFiles}
      />
      <ServiceExportDrawer
        isOpen={isExportDrawerOpen}
        onClose={handleCloseExportDrawer}
        filterCriteria={filterCriteria}
        searchCriteria={props.searchCriteria}
      />
    </div>
  );
};

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    width: '100%',
    height: '100%',
    overflow: 'hidden'
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    alignItems: 'center'
  },
  exportActionContainer: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  loading: {
    padding: tokens.spacingVerticalXL,
    textAlign: 'center'
  },
  tableWrapper: {
    width: '100%',
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: 'calc(100vh - 250px)',
    position: 'relative',
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadow4,
    backgroundColor: tokens.colorNeutralBackground1
  },
  tableHeader: {
    backgroundColor: tokens.colorNeutralBackground2,
    fontWeight: tokens.fontWeightSemibold
  },
  tableRow: {
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover
    }
  },
  // Row that will receive the dropped files: selected-row look of the guide (tenue primary + left bar)
  dropRow: {
    backgroundColor: 'var(--ichtys-row-selected-bg)',
    boxShadow: 'inset 4px 0 0 var(--ichtys-row-selected-bar)',
    ...shorthands.outline('2px', 'dashed', 'var(--ichtys-row-selected-bar)'),
    outlineOffset: '-2px'
  },
  // Floating hint while files are dragged over the grid (sticky: the rows do not move)
  dropHint: {
    position: 'sticky',
    top: 0,
    left: 0,
    zIndex: 2,
    pointerEvents: 'none',
    height: 0,
    overflow: 'visible',
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS
  },
  tableCell: {
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS
  },
  flagsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
    rowGap: tokens.spacingVerticalXXS
  },
  // Cell text hierarchy
  cellPrimary: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    lineHeight: tokens.lineHeightBase300
  },
  cellCost: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorBrandForeground1,
    lineHeight: tokens.lineHeightBase300
  },
  cellSecondary: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    lineHeight: tokens.lineHeightBase200,
    marginTop: '2px'
  },
  cellCaption: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground4,
    lineHeight: tokens.lineHeightBase100,
    marginTop: '2px',
    fontStyle: 'italic'
  },
  cellEmpty: {
    color: tokens.colorNeutralForegroundDisabled,
    fontSize: tokens.fontSizeBase300
  },
  // Summary bar
  summaryBar: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: tokens.spacingHorizontalXS
  },
  summaryText: {
    color: tokens.colorNeutralForeground3
  },
  // Empty state
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: `${tokens.spacingVerticalXXXL} ${tokens.spacingHorizontalXL}`,
    gap: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadow2
  },
  emptyStateIcon: {
    fontSize: '48px',
    color: tokens.colorNeutralForeground4
  },
  emptyStateTitle: {
    color: tokens.colorNeutralForeground2
  }
});

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(ServiceListComponent as any);
